/**
 * Detection utilities for the Pi Browser and Pi Network platform contexts.
 */

let sdkHostConfirmed = false

export function confirmPiSDKHost(): void {
  sdkHostConfirmed = true
}

function isPiURL(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && /(^|\.)(minepi\.com|pinet\.com)$/.test(url.hostname)
  } catch {
    return false
  }
}

/** Ask the SDK first; a missing, failed, or slow host uses the browser hints. */
export async function detectPiBrowser(): Promise<boolean> {
  if (typeof window === 'undefined') return false
  let timeout: ReturnType<typeof setTimeout> | undefined
  try {
    if (window.Pi?.getPiHostAppInfo) {
      const info = await Promise.race([
        window.Pi.getPiHostAppInfo(),
        new Promise<null>((resolve) => {
          timeout = setTimeout(() => resolve(null), 3000)
        }),
      ])
      if (info?.hostApp === 'pi-browser') confirmPiSDKHost()
    }
  } catch {
    // Standard browsers can load the SDK without having a Pi messaging host.
  } finally {
    clearTimeout(timeout)
  }
  return isInsidePiBrowser()
}

/** Framed by a Pi page, e.g. the Developer Portal's desktop sandbox. */
function isEmbeddedByPi(): boolean {
  try {
    if (window.parent === window) return false
    return isPiURL(document.referrer) || Array.from(window.location.ancestorOrigins || []).some(isPiURL)
  } catch {
    return false
  }
}

/**
 * True on a laptop or desktop computer, where no Pi payment can be made: Pi
 * Browser is a phone app. Pi's desktop sandbox (an iframe) still pays, and
 * test overrides opt out.
 *
 * Deliberately ignores the referrer, platform and SDK hints that
 * isInsidePiBrowser() trusts: a desktop browser that arrived from a minepi.com
 * link passes those, and the SDK then tries to launch Pi Browser's app link.
 */
export function isDesktopBrowser(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  if (window.__PI_MOCK__ || window.__PI_BROWSER__) return false
  const ua = navigator.userAgent || ''
  if (/pi[\s_-]?browser|Android|iPhone|iPad|iPod|Mobile/i.test(ua)) return false
  // iPadOS Safari reports a Mac user agent; only the touch screen gives it away.
  if (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1) return false
  return !isEmbeddedByPi()
}

/**
 * Detect whether the app is running in a recognized Pi-hosted environment.
 * Checks for iframe embeddings by Pi Network, sandbox query flags, or Pi domains.
 */
export function isPiPlatformContext(): boolean {
  if (typeof window === 'undefined') return false
  try {
    // 1. Embedded inside Pi webview/iframe
    if (isEmbeddedByPi()) return true
    // 2. Query flags when launched from Developer Portal sandbox
    const search = window.location.search || ''
    if (/[?&](pi_sandbox|sandbox)=1\b/.test(search)) {
      return true
    }
    // 3. Running under Pi app domain
    if (isPiURL(window.location.origin)) {
      return true
    }
  } catch {
    // Cross-origin access restrictions
  }
  return false
}

/**
 * Detect whether the app is running inside the Pi Browser or a Pi-hosted runtime.
 * We inspect:
 * 1. Explicit test overrides (window.__PI_BROWSER__, window.__PI_MOCK__)
 * 2. Pi Browser user-agent token (navigator.userAgent containing "PiBrowser")
 * 3. Pi platform / sandbox context hints
 *
 * We specifically do NOT treat window.Pi alone as proof of Pi Browser because
 * loading the script creates that object in standard browsers too.
 */
export function isInsidePiBrowser(): boolean {
  if (typeof window === 'undefined') return false
  if (sdkHostConfirmed || window.__PI_MOCK__ || window.__PI_BROWSER__) return true

  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || ''
  if (/pi[\s_-]?browser/i.test(ua)) return true

  if (isPiURL(document.referrer) || isPiPlatformContext()) return true

  return false
}

/**
 * Returns a deeplink URI to open a given route in the Pi Browser (e.g. `pi://talentswap.shop/order/:id/`).
 * On mobile devices with Pi Browser installed, navigating to this URI switches to the Pi Browser app
 * and navigates to the specified target path.
 */
export function getPiBrowserDeeplink(path: string): string {
  let host = 'talentswap.shop'
  const configured = (import.meta.env.VITE_PI_APP_URL || import.meta.env.VITE_APP_URL) as string | undefined
  if (configured) {
    try {
      const url = new URL(configured.startsWith('http') ? configured : `https://${configured}`)
      if (url.host && !/^(localhost|127\.0\.0\.1)/.test(url.hostname)) {
        host = url.host
      }
    } catch {
      // Keep default host
    }
  }

  if (typeof window !== 'undefined' && window.location?.host) {
    const hostname = window.location.hostname
    if (hostname && !/^(localhost|127\.0\.0\.1)$/.test(hostname)) {
      host = window.location.host
    }
  }

  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `pi://${host}${cleanPath}`
}

